import React from 'react';
import { FileDrop } from 'react-file-drop';
import './bulkUpload.css';
import * as api from 'src/api/Api';
import { IBulkUploadResponse, InsertResult, LoadException } from 'src/types/IBulkUploadResponse';
import { Box, Button, CircularProgress, Paper, Grid } from '@mui/material';

//import sample json to test with
//import testUpload from "../../../api/SampleResponses/upload-response-fail.json";

const BulkUserUpload: React.FC = () => {
  const [file, setFile] = React.useState<File | undefined>();
  const [isLoading, setIsLoading] = React.useState<Boolean>(false);
  const [errors, setErrors] = React.useState<string[]>([]);
  const [status, setStatus] = React.useState<string>('');

  function resetStatus() {
    setErrors([]);
    setStatus('');
    setIsLoading(false);
    setFile(undefined);
  }

  function checkFileType(f: File): boolean {
    let extension = f.name.split('.').pop();

    if (extension === 'csv' || extension === 'xlsx') {
      return true;
    } else {
      setErrors(['File must be .csv or .xlsx, please upload again']);
      return false;
    }
  }

  function handleInput(e: any) {
    resetStatus();
    let f: File = e.target.files[0];
    if (checkFileType(f)) {
      setFile(f);
    }
  }

  function handleFileDrop(files: FileList | null) {
    resetStatus();
    if (files !== null && files.length > 0 && checkFileType(files[0])) {
      setFile(files[0]);
    }
  }

  function handlePreflightErrors(e: LoadException[]) {
    if (!e || e.length === 0) {
      return;
    }

    let errorList: string[] = [];

    e.forEach((err) => {
      let rowNum: number = err.rowNumber;
      let message: string = '';
      err.detail.exceptions.forEach((er) => {
        message += er.message;
      });

      errorList.push('Row Num - ' + rowNum + ' ' + message);
    });

    setErrors((prev) => [...prev, ...errorList]);
  }

  function handleBulkInsertErrors(badInsertResults: InsertResult[]) {
    if (badInsertResults && badInsertResults.length > 0) {
      let errorList: string[] = [];

      badInsertResults.forEach((r) => {
        let errorStr = 'Failed: ' + r.user.emailAddress + ' VendorNo: ' + r.user.vendorNumber + ' Message: ' + r.response.response;
        errorList.push(errorStr);
      });

      setErrors(errorList);
    }
  }

  async function uploadFile() {
    setIsLoading(true);

    if (!file) {
      alert('Please select a file');
      return;
    }

    api
      .postExcelFile<LoadException[]>('/User/PreflightBulkUploadVendors', file)
      .then((r) => {
        if (r && r.length !== 0) {
          setIsLoading(false);
          handlePreflightErrors(r);
          return;
        }

        api
          .postExcelFile<IBulkUploadResponse>('/User/BulkUploadVendors', file)
          .then((res) => {
            if (res?.insertResults?.length && res.insertResults.length > 0) {
              var successList: InsertResult[] = res.insertResults.filter((x) => x.response.success === true);
              var failList: InsertResult[] = res.insertResults.filter((x) => x.response.success === false);

              if (res.emailErrors && res.emailErrors.length > 0) {
                setErrors((prev) => [...prev, ...res.emailErrors]);
              }

              setStatus(`${successList.length} Rows Inserted. ${res.emailsSent} Welcome Emails Sent. ${failList.length} Errors.`);
              handleBulkInsertErrors(failList);
            } else {
              setStatus('Zero Rows Inserted');
            }
          })
          .catch((err) => {
            setErrors(['BulkUploadVendors ' + err]);
          })
          .finally(() => {
            setIsLoading(false);
          });
      })
      .catch((e) => {
        setIsLoading(false);
        setErrors(['PreflightBulkUploadVendors ' + e]);
      });
  }

  return (
    <Grid container justifyContent={'center'} maxWidth={1200} sx={{ margin: '0 auto' }}>
      <Grid item md={2}></Grid>
      <Grid item md={8}>
        <Paper elevation={3}>
          <h1 style={{ textAlign: 'center', paddingTop: '15px', marginTop: '30px' }}>Bulk Upload Users</h1>
          <div style={{ border: '1px solid black', color: 'black', height: '200px', backgroundColor: 'rgba(140, 176, 120, 0.55)' }}>
            <FileDrop onDrop={(files) => handleFileDrop(files)}>
              <div style={{ paddingBottom: '10px' }}>Drop csv or xlsx files here! </div>
              <img src={`${process.env.PUBLIC_URL}/static/icons/upload.png`} alt={'uploadImg'} style={{ paddingBottom: '10px', width: '10%' }} />
              <Button variant={'outlined'} color="primary" component="label" style={{ backgroundColor: '#fff' }}>
                Or Select a File
                <input type="file" hidden accept=".csv, .xlsx" onChange={handleInput} />
              </Button>
            </FileDrop>
          </div>

          <Box>
            <div>{isLoading ? <CircularProgress /> : null}</div>

            {file && file.name.length > 0 && (
              <div
                style={{
                  color: '#00529B',
                  backgroundColor: '#BDE5F8',
                  margin: 5,
                  padding: 5,
                }}
              >
                {file ? file.name : ''}
              </div>
            )}

            {status.length > 0 && (
              <div
                style={{
                  color: '#4F8A10',
                  backgroundColor: '#DFF2BF',
                  margin: 5,
                  padding: 5,
                }}
              >
                {status}
              </div>
            )}

            {errors.length > 0 && (
              <div
                style={{
                  backgroundColor: '#FFD2D2',
                  color: '#D8000C',
                  margin: 5,
                  padding: 5,
                }}
              >
                {errors.map((e, i) => (
                  <p key={i}>{e}</p>
                ))}
              </div>
            )}
          </Box>

          <Box style={{ padding: '15px' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                uploadFile();
              }}
            >
              Upload File
            </Button>

            <a style={{ paddingLeft: '15px' }} href={process.env.PUBLIC_URL + '/TemplateCSV.csv'} download>
              Download Template
            </a>
          </Box>

          <div style={{ padding: '15px' }}>
            <b>Instructions:</b>
            <ul>
              <li>Download the template</li>
              <li>Change the values as needed. Use unique usernames or you will get an error.</li>
              <li>For AppRole column use the following values</li>
              <ul>
                <li>15 = Vendor Portal User</li>
                <li>34 = Vendor Portal Admin</li>
              </ul>
              <li>A welcome email will be sent to each new user.</li>
              <ul>
                <li>If the email cannot be sent you will get an error message</li>
                <li>If this is the only error then the user will still have been created successfully</li>
              </ul>
            </ul>
          </div>
        </Paper>
      </Grid>
      <Grid item md={2}></Grid>
    </Grid>
  );
};

export default BulkUserUpload;
